/**
 * PlatformDashboard.jsx — Schoon, premium, mode-aware klant dashboard.
 *
 * Layout:
 *  1. Welkomstheader  (naam + bedrijf + role-badge + PDF download)
 *  2. ConnectionStatus banner  (als geen integraties gekoppeld)
 *  3. Score-hero card (groot)  +  3 quick-stat cards
 *  4. Volgende stap card (eerstvolgende hoge-prioriteit tip met Huidig→Beter)
 *  5. Top opdrachten grid (Huidig→Beter mini-cards)
 *  6. Integraties summary panel
 *  7. AI activity feed  (alleen voor beheer_paid)
 *  8. Upgrade CTA  (alleen voor radar_free)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import {
  ArrowRight, CheckCircle, AlertTriangle, Globe, Search, MapPin,
  Star, Target, Zap, Lock, Clock, TrendingUp, Award, Download,
  MousePointerClick, BookOpen, Headphones, Link2, ChevronRight,
  Crown, Sparkles, RefreshCw, Bell,
} from 'lucide-react';
import EmailVerifyBanner from '../../components/platform/EmailVerifyBanner';

// ── Helpers ──────────────────────────────────────────────────────────────

const ACTIE_TYPES = {
  one_click: { icon: MousePointerClick, label: '1-Click', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  guided: { icon: BookOpen, label: 'Guided', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  managed: { icon: Headphones, label: 'Managed', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
};

const ActieTypeBadge = ({ type, size = 'sm' }) => {
  const cfg = ACTIE_TYPES[type];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const isSm = size === 'sm';
  return (
    <span data-testid={`actie-type-badge-${type}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: isSm ? 3 : 5,
      padding: isSm ? '2px 6px' : '3px 8px', borderRadius: 5, fontSize: isSm ? 9 : 10,
      fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      <Icon size={isSm ? 10 : 12} /> {cfg.label}
    </span>
  );
};

const Ring = ({ score, size = 56 }) => {
  const r = size / 2 - 5, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const c = score >= 70 ? '#22c55e' : score >= 50 ? '#D4A017' : '#ef4444';
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="6" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth="6"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 5} textAnchor="middle" fill={c} fontSize={size > 80 ? 24 : 14} fontWeight="800">{score}</text>
    </svg>
  );
};

const ROLE_LABEL = {
  radar_free: { label: 'Gratis account', color: '#94a3b8', bg: '#f1f5f9' },
  radar_paid: { label: 'Connected Self-Service', color: '#0f172a', bg: '#fef3c7' },
  beheer_paid: { label: 'Managed AI Bot', color: '#7c3aed', bg: '#f5f3ff' },
  admin: { label: 'Admin', color: '#0f172a', bg: '#f1f5f9' },
  manager: { label: 'Manager', color: '#0f172a', bg: '#f1f5f9' },
};

// ── Billing Phase Card ──────────────────────────────────────────────────

function BillingPhaseCard({ billing }) {
  if (!billing) return null;
  const isFree = billing.plan_state === 'free_scan';
  if (isFree) return null;
  const isLaunch = billing.billing_phase === 'launch_month';
  // Nederlandse opmaak met duizendtal-punt en komma decimaal
  const formatEUR = (n) => `€${(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div data-testid="billing-phase-card" style={{
      background: isLaunch ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : '#f8fafc',
      border: `1px solid ${isLaunch ? '#fde68a' : '#e2e8f0'}`,
      borderRadius: 14, padding: 18, marginBottom: 16,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 220 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: isLaunch ? '#D4A017' : '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
          {isLaunch ? <Sparkles size={18} /> : <CheckCircle size={18} />}
        </div>
        <div>
          <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>
            Huidig plan
          </p>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a' }} data-testid="billing-plan-label">
            {billing.plan_label}
          </p>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }} data-testid="billing-current-price">
            {formatEUR(billing.current_amount_ex_vat)}{billing.current_amount_ex_vat > 0 ? '/mnd excl. btw' : ''}
            {isLaunch && billing.days_until_recurring !== null && (
              <span> · nog {billing.days_until_recurring} dagen in launch-maand</span>
            )}
          </p>
        </div>
      </div>
      {isLaunch && billing.next_amount_ex_vat ? (
        <div data-testid="billing-next-info" style={{ textAlign: 'right', minWidth: 200 }}>
          <p style={{ fontSize: 10, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            Volgende factuur
          </p>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
            {formatEUR(billing.next_amount_ex_vat)}/mnd excl. btw
          </p>
          <p style={{ fontSize: 11, color: '#92400e' }}>
            ({formatEUR(billing.next_amount_incl_vat)} incl. btw)
          </p>
        </div>
      ) : (
        <Link to="/platform/instellingen" data-testid="billing-manage-link" style={{
          color: '#0f172a', fontSize: 12, fontWeight: 700, textDecoration: 'none',
          border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: 8, background: 'white',
        }}>Beheer abonnement <ChevronRight size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /></Link>
      )}
    </div>
  );
}

// ── Trial Status Widget ────────────────────────────────────────────────

function TrialStatusWidget({ api }) {
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    api('get', '/api/platform/my-trial').then((r) => {
      if (r.data?.has_trial) setTrial(r.data);
    }).catch(() => { /* geen trial */ });
  }, [api]);

  if (!trial) return null;

  const daysLeft = trial.days_left ?? 0;
  const isUrgent = daysLeft <= 7;
  const bg = isUrgent
    ? 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)'
    : 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
  const border = isUrgent ? '#fdba74' : '#86efac';
  const accent = isUrgent ? '#c2410c' : '#047857';

  return (
    <div data-testid="trial-status-widget" style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 14, padding: 18, marginBottom: 16,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 220 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
          <Clock size={18} />
        </div>
        <div>
          <p style={{ fontSize: 10, color: accent, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>
            30-dagen trial actief
          </p>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a' }} data-testid="trial-days-left">
            Nog {daysLeft} {daysLeft === 1 ? 'dag' : 'dagen'} gratis
          </p>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Tot {new Date(trial.einddatum).toLocaleDateString('nl-NL')} · Self-Service Connected toegang
          </p>
        </div>
      </div>
      <Link to="/platform/checkout" data-testid="trial-upgrade-cta" style={{
        background: accent, color: 'white', fontWeight: 700, fontSize: 13,
        padding: '10px 18px', borderRadius: 10, textDecoration: 'none', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
        Upgrade naar betaald <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

/** Compact Huidig→Beter tip card voor het dashboard. */
function MiniTipCard({ opdracht, locked }) {
  const huidig = opdracht.huidige_situatie || '';
  const beter = opdracht.beter_voorstel || '';
  const pagina = opdracht.pagina || 'site-breed';
  const mode = opdracht.execution_mode || opdracht.actie_type || 'guided';

  return (
    <Link
      to={locked ? '/platform/checkout' : `/platform/opdrachten/${opdracht.id}`}
      data-testid={`tip-card-${opdracht.id || opdracht.template_id}`}
      style={{
        display: 'block', position: 'relative', textDecoration: 'none',
        background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '14px 16px', transition: 'all 0.15s', overflow: 'hidden',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4A017'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(212,160,23,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Locked overlay */}
      {locked && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 6, zIndex: 2,
        }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={16} style={{ color: '#D4A017' }} />
          </div>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>Upgrade om te ontgrendelen</p>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
        <p style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
          📍 {pagina}
        </p>
        <ActieTypeBadge type={mode} />
      </div>

      <h4 style={{ fontFamily: "'Sora', sans-serif", fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8, lineHeight: 1.35 }}>
        {opdracht.title}
      </h4>

      {huidig && (
        <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '6px 9px', marginBottom: 4 }}>
          <p style={{ fontSize: 8, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 1 }}>Huidig</p>
          <p style={{ fontSize: 11, color: '#0f172a', fontStyle: 'italic', lineHeight: 1.35 }}>
            "{huidig.slice(0, 90)}{huidig.length > 90 ? '…' : ''}"
          </p>
        </div>
      )}
      {beter && (
        <div style={{ background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 6px 6px 0', padding: '6px 9px' }}>
          <p style={{ fontSize: 8, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 1 }}>Beter voorstel</p>
          <p style={{ fontSize: 11, color: '#0f172a', fontWeight: 600, lineHeight: 1.35 }}>
            {beter.slice(0, 100)}{beter.length > 100 ? '…' : ''}
          </p>
        </div>
      )}
    </Link>
  );
}

/** Connection-status banner als er geen integraties gekoppeld zijn. */
function ConnectionBanner({ connectedCount, totalProviders = 7 }) {
  if (connectedCount > 0) return null;
  return (
    <div data-testid="connection-banner" style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
      borderRadius: 14, padding: '18px 22px', marginBottom: 20,
      color: 'white', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(212,160,23,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Link2 size={20} style={{ color: '#D4A017' }} />
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <p style={{ fontSize: 14, fontWeight: 800 }}>Koppel uw accounts om uw volledige potentieel te ontgrendelen</p>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
          Verbind Google, Meta, TikTok en uw website-platform voor live data, AI optimalisatie en automatische verbeteringen.
        </p>
      </div>
      <Link to="/platform/integraties" data-testid="connection-banner-cta" style={{
        background: '#D4A017', color: '#0f172a', fontWeight: 700, fontSize: 13,
        padding: '10px 18px', borderRadius: 10, textDecoration: 'none', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
        Accounts koppelen <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/** Banner voor klanten zonder website of die redesign nodig hebben. */
function WebsiteBuildBanner({ scan, company }) {
  // Toon als website ontbreekt of als scan een "no_website" of "redesign_required" verdict heeft
  const website = (company?.website || scan?.website_url || '').trim();
  const noWebsite = !website || website === 'https://example.com';
  const needsRedesign = scan?.redesign_verdict && ['no_website', 'redesign_required', 'major_improvements'].includes(scan.redesign_verdict);
  if (!noWebsite && !needsRedesign) return null;
  return (
    <div data-testid="website-build-banner" style={{
      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      border: '1px solid #fde68a', borderRadius: 14, padding: '16px 20px',
      marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 240 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: '#D4A017', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
          <Sparkles size={20} />
        </div>
        <div>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {noWebsite ? 'Geen website?' : 'Tijd voor een redesign?'}
          </p>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
            {noWebsite ? 'Bouw mijn website met AI' : 'Plan een redesign met AI'}
          </p>
          <p style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
            Genereer een complete blueprint: sitemap, content, design en SEO opzet.
          </p>
        </div>
      </div>
      <Link to="/platform/website-bouw" data-testid="website-build-cta" style={{
        background: '#0f172a', color: 'white', fontWeight: 700, fontSize: 13,
        padding: '11px 20px', borderRadius: 10, textDecoration: 'none', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
        Start blueprint <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/** Volgende stap card — toont de eerstvolgende high-priority tip groot. */
function NextStepCard({ opdracht }) {
  if (!opdracht) return null;
  const huidig = opdracht.huidige_situatie || '';
  const beter = opdracht.beter_voorstel || '';
  const mode = opdracht.execution_mode || opdracht.actie_type || 'guided';

  return (
    <div data-testid="next-step-card" style={{
      background: 'white', borderRadius: 14, padding: '20px 22px', marginBottom: 20,
      border: '1px solid #f1f5f9', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 200, height: 200,
        background: 'radial-gradient(circle, rgba(212,160,23,0.08), transparent)', pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #D4A017, #f5c842)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={17} style={{ color: '#0f172a' }} />
          </div>
          <div>
            <p style={{ fontSize: 10, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>Volgende stap voor uw groei</p>
            <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{opdracht.title}</h2>
          </div>
        </div>
        <ActieTypeBadge type={mode} size="lg" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }} className="next-step-grid">
        {huidig && (
          <div style={{ background: '#fef2f2', borderLeft: '4px solid #f87171', borderRadius: '0 10px 10px 0', padding: '10px 14px' }}>
            <p style={{ fontSize: 9, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>Huidig op uw site</p>
            <p style={{ fontSize: 12, color: '#0f172a', fontStyle: 'italic', lineHeight: 1.5 }}>"{huidig}"</p>
          </div>
        )}
        {beter && (
          <div style={{ background: '#fff8e1', borderLeft: '4px solid #D4A017', borderRadius: '0 10px 10px 0', padding: '10px 14px' }}>
            <p style={{ fontSize: 9, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>Beter voorstel</p>
            <p style={{ fontSize: 12, color: '#0f172a', fontWeight: 600, lineHeight: 1.5 }}>{beter}</p>
          </div>
        )}
      </div>

      <Link to={`/platform/opdrachten/${opdracht.id}`} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0f172a',
        color: 'white', fontSize: 12, fontWeight: 700, padding: '9px 18px',
        borderRadius: 10, textDecoration: 'none',
      }}>
        Bekijk volledige instructie <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/** Integrations summary panel — toont 7-8 belangrijkste integraties als compact lijstje. */
function IntegrationsPanel({ connected }) {
  const KEY_INTEGRATIONS = [
    { id: 'google_business', name: 'Google Bedrijfsprofiel', icon: '📍' },
    { id: 'google_analytics', name: 'Google Analytics', icon: '📊' },
    { id: 'google_ads', name: 'Google Ads', icon: '📢' },
    { id: 'google_search_console', name: 'Search Console', icon: '🔍' },
    { id: 'meta_facebook', name: 'Facebook / Meta', icon: '📘' },
    { id: 'meta_instagram', name: 'Instagram', icon: '📸' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵' },
    { id: 'wordpress', name: 'WordPress / Shopify', icon: '🌐' },
  ];

  return (
    <div data-testid="integrations-panel" style={{
      background: 'white', borderRadius: 14, border: '1px solid #f1f5f9', padding: '16px 18px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link2 size={15} style={{ color: '#D4A017' }} />
          <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Uw koppelingen</p>
        </div>
        <Link to="/platform/integraties" style={{ fontSize: 11, fontWeight: 600, color: '#D4A017', textDecoration: 'none' }}>
          Beheren →
        </Link>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {KEY_INTEGRATIONS.map(i => {
          const isConnected = connected[i.id];
          return (
            <div key={i.id} style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px',
              borderRadius: 8, background: isConnected ? '#f0fdf4' : '#f8fafc',
              border: isConnected ? '1px solid #bbf7d0' : '1px solid #f1f5f9',
            }}>
              <span style={{ fontSize: 14 }}>{i.icon}</span>
              <p style={{ flex: 1, fontSize: 11, fontWeight: 600, color: '#0f172a', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {i.name}
              </p>
              {isConnected ? (
                <CheckCircle size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
              ) : (
                <Link to="/platform/integraties" style={{ fontSize: 10, fontWeight: 700, color: '#D4A017', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  Koppel →
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** AI activity feed snippet (alleen beheer_paid) */
function AiFeed({ items }) {
  const fallback = [
    { text: 'Google Bedrijfsprofiel beschrijving bijgewerkt met zoekwoord-strategie', when: 'Vandaag · 09:42', mode: 'auto' },
    { text: '3 backlink-aanvragen verstuurd (Trustpilot, Gouden Gids, Telefoonboek.nl)', when: 'Gisteren · 14:18', mode: 'auto' },
    { text: 'Nieuwe H1 voorgesteld voor /diensten — wacht op uw goedkeuring', when: 'Gisteren · 11:05', mode: 'approval' },
  ];
  const list = (items && items.length ? items : fallback).slice(0, 4);
  return (
    <div data-testid="ai-feed-snippet" style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
      borderRadius: 14, padding: '18px 22px', color: 'white',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(212,160,23,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap size={15} style={{ color: '#D4A017' }} />
          </div>
          <div>
            <p style={{ fontSize: 12, fontWeight: 800 }}>AI Activity Feed</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>Wat onze bot deze week voor u deed</p>
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: 16, padding: '3px 9px', fontSize: 10, fontWeight: 700 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e' }} /> Live
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {list.map((a, i) => {
          const isApproval = a.mode === 'approval';
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '8px 12px',
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: isApproval ? 'rgba(212,160,23,0.2)' : 'rgba(34,197,94,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {isApproval ? <Clock size={11} style={{ color: '#D4A017' }} /> : <CheckCircle size={11} style={{ color: '#22c55e' }} />}
              </div>
              <p style={{ flex: 1, fontSize: 11, lineHeight: 1.4 }}>{a.text}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>{a.when}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────

export default function PlatformDashboard() {
  const { api, user } = useAuth();
  const currentPlan = usePlan();
  const [data, setData] = useState(null);
  const [connected, setConnected] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api('get', '/api/platform/dashboard').catch(() => ({ data: null })),
      api('get', '/api/platform/integrations').catch(() => ({ data: { integrations: [] } })),
    ]).then(([dash, ints]) => {
      setData(dash.data);
      const map = {};
      (ints.data?.integrations || []).forEach(i => { map[i.provider] = i; });
      setConnected(map);
    }).finally(() => setLoading(false));
  }, [api]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const resp = await api('get', '/api/platform/report/pdf', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'imperia_ai_groeiscan_rapport.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download mislukt:', e);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  const scan = data?.scan;
  const analyse = scan?.analyse || {};
  const company = data?.company;
  const score = analyse.overall_score || 0;
  const opdrachten = data?.opdrachten || [];
  const role = user?.role || 'radar_free';
  // Source of truth: plan_state (van backend of afgeleid door usePlan)
  const plan = currentPlan; // alias voor leesbaarheid hieronder
  const isPaid = plan.isPaid;
  const isManaged = plan.isManaged;
  const isFree = plan.isFree;

  const roleConfig = ROLE_LABEL[role] || ROLE_LABEL.radar_free;
  const connectedCount = Object.keys(connected).length;

  const hoogPrioCount = opdrachten.filter(o => o.priority === 'hoog' || o.priority === 'kritiek').length;
  const doneCount = opdrachten.filter(o => o.status === 'done').length;
  const openCount = opdrachten.length - doneCount;

  // Sorteer op prioriteit voor weergave
  const PRIO_RANK = { kritiek: 0, hoog: 1, middel: 2, laag: 3 };
  const sortedOps = [...opdrachten]
    .filter(o => o.status !== 'done')
    .sort((a, b) => (PRIO_RANK[a.priority] ?? 9) - (PRIO_RANK[b.priority] ?? 9));

  const nextStep = sortedOps[0];
  const FREE_VISIBLE = 3;
  const PAID_VISIBLE = 6;
  const visibleOps = sortedOps.slice(0, isPaid ? PAID_VISIBLE : FREE_VISIBLE);
  const lockedOps = isFree ? sortedOps.slice(FREE_VISIBLE, FREE_VISIBLE + 3) : [];

  return (
    <div data-testid="platform-dashboard">
      <EmailVerifyBanner />

      {/* ── 1. Welkomstheader ── */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
            Welkom{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <p style={{ color: '#64748b', fontSize: 13 }}>{company?.name || 'Uw bedrijf'}</p>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
              padding: '3px 8px', borderRadius: 5, color: roleConfig.color, background: roleConfig.bg,
            }}>
              {isManaged && <Crown size={10} />} {roleConfig.label}
            </span>
          </div>
        </div>
        {scan && (
          <button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={pdfLoading} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#0f172a', color: 'white', border: 'none',
            borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', opacity: pdfLoading ? 0.6 : 1,
          }}>
            <Download size={14} /> {pdfLoading ? 'Genereren...' : 'PDF rapport'}
          </button>
        )}
      </div>

      {/* ── 2. Billing phase card (paid users only) ── */}
      <BillingPhaseCard billing={data?.billing} />

      {/* ── 2a. Trial status widget (FASE 2) ── */}
      <TrialStatusWidget api={api} />

      {/* ── 2b. Website build banner (geen site of redesign nodig) ── */}
      <WebsiteBuildBanner scan={data?.scan} company={data?.company} />

      {/* ── 3. Connection banner ── */}
      <ConnectionBanner connectedCount={connectedCount} />

      {/* ── 3. Score-hero + 3 quick stats ── */}
      <div className="dash-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 16 }}>
          <Ring score={score} size={84} />
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Online vindbaarheid</p>
            <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
              {analyse.score_label || 'Analyseren...'}
            </p>
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{openCount} open · {doneCount} klaar</p>
          </div>
        </div>
        <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #f1f5f9', textAlign: 'center' }}>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{opdrachten.length}</p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Opdrachten totaal</p>
        </div>
        <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #f1f5f9', textAlign: 'center' }}>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 30, fontWeight: 800, color: '#ef4444', lineHeight: 1 }}>{hoogPrioCount}</p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Hoge prioriteit</p>
        </div>
        <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #f1f5f9', textAlign: 'center' }}>
          <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 30, fontWeight: 800, color: '#22c55e', lineHeight: 1 }}>{connectedCount}</p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Accounts gekoppeld</p>
        </div>
      </div>

      {/* ── 4. Volgende stap ── */}
      <NextStepCard opdracht={nextStep} />

      {/* ── 5. Tips grid + 6. Integrations ── */}
      <div className="dash-content-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #f1f5f9', padding: '16px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Zap size={15} style={{ color: '#D4A017' }} />
                <p style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                  {isFree ? `Top ${FREE_VISIBLE} tips (van ${opdrachten.length})` : 'Top verbeterpunten'}
                </p>
              </div>
              <Link to="/platform/opdrachten" style={{ fontSize: 11, fontWeight: 600, color: '#D4A017', textDecoration: 'none' }}>
                Alle bekijken →
              </Link>
            </div>

            {visibleOps.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                {scan ? 'Geen openstaande opdrachten' : 'Eerste scan wordt uitgevoerd...'}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }} className="tips-grid">
                {visibleOps.map(o => <MiniTipCard key={o.id || o.template_id} opdracht={o} />)}
                {lockedOps.map(o => <MiniTipCard key={o.id || o.template_id} opdracht={o} locked />)}
              </div>
            )}

            {isFree && opdrachten.length > FREE_VISIBLE && (
              <div data-testid="upgrade-prompt" style={{
                marginTop: 14, padding: '14px 16px', borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(212,160,23,0.08), rgba(212,160,23,0.02))',
                border: '1px solid rgba(212,160,23,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <Lock size={18} style={{ color: '#D4A017', flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>Nog {opdrachten.length - FREE_VISIBLE} tips beschikbaar</p>
                    <p style={{ fontSize: 11, color: '#64748b' }}>Upgrade voor onbeperkte tips + maandelijkse her-scan + 1-klik fixes</p>
                  </div>
                </div>
                <Link to="/platform/checkout" data-testid="upgrade-cta" style={{
                  background: '#D4A017', color: '#0f172a', fontWeight: 800, fontSize: 12,
                  padding: '9px 16px', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap',
                }}>
                  Upgrade — €14,95 excl. btw →
                </Link>
              </div>
            )}
          </div>
        </div>

        <IntegrationsPanel connected={connected} />
      </div>

      {/* ── 7. AI activity feed (alleen managed) ── */}
      {isManaged && (
        <div style={{ marginBottom: 20 }}>
          <AiFeed items={data?.ai_activity} />
        </div>
      )}

      {/* ── 8. Upgrade CTA (alleen free) ── */}
      {isFree && (
        <div data-testid="dashboard-upgrade-cta" style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
          borderRadius: 14, padding: '24px 28px', color: 'white',
          display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
        }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(212,160,23,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Sparkles size={24} style={{ color: '#D4A017' }} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 4 }}>
              Klaar voor volgende stap?
            </p>
            <p style={{ fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
              Koppel uw accounts voor account-specifieke AI-tips
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
              Connected Self-Service v.a. €14,95/mnd excl. btw · of Managed AI Bot v.a. €995,95 excl. btw eerste maand (daarna €2.495,95/mnd excl. btw)
            </p>
          </div>
          <Link to="/platform/checkout" style={{
            background: '#D4A017', color: '#0f172a', fontWeight: 800, fontSize: 14,
            padding: '12px 22px', borderRadius: 10, textDecoration: 'none', whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            Bekijk pakketten <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
}
